import React, { useState } from 'react';
import PropTypes from 'prop-types';

import './index.scss';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import UploadZone from '@/components/UploadZone';
import ButtonComponent from '@/components/Button';
import { ReactComponent as DownloadIcon } from '@/assets/images/icons/download.svg';
import { downloadUrl } from '@/utils/download';
import { formatCurrency, parseDateTime } from '@/utils/formatter';
import { TRANSACTION_STATUSES } from '@/constants';
import { alertSuccess } from '@/utils/notification';
import { TransactionService } from '@/services';

function TransactionDetailContainer({ data }) {
  const { t } = useTranslation();
  const [slip, setSlip] = useState();
  const onChangeFile = (file) => {
    setSlip(file);
  };
  const [loading, setLoading] = useState(false);
  const handleSubmit = async () => {
    setLoading(true);
    const res = await TransactionService.uploadSlip({
      file: slip,
      id: data.id,
    });
    if (!res.success) {
      setLoading(false);
      return;
    }
    alertSuccess('UPLOAD_SLIP_SUCCESS');
    window.location.reload();
    setLoading(false);
  };

  const handleOpenImage = () => {
    window.open(data.paymentSlip);
  };

  const handleDownload = () => {
    downloadUrl(data.invoiceFile, 'invoice');
    // TODO handle download invoice
  };

  const [resubmit, setResubmit] = useState(false);
  const isRejected =
    data.transactionStatus === TRANSACTION_STATUSES.rejected.text;

  const subTotal = parseFloat(data.amountTotal) - parseFloat(data.amountVat);
  const address = [
    data.customerAddress,
    data.customerWard?.name,
    data.customerDistrict?.name,
    data.customerProvince?.name,
    data.customerCountry?.name,
  ].join(', ');
  return (
    <Row gutter={[16, 24]} className="transaction-detail">
      <Col
        xxl={{ span: 16 }}
        md={{ span: 14 }}
        span={24}
        className="transaction-detail__group-cart"
      >
        <div className="form-cart">
          <h3 className="form-cart__title">
            {t('transactions_page.detail.bill.title')}
          </h3>
          <div className="form-cart__item">
            <p className="form-cart__item__label">
              {t('transactions_page.detail.bill.plan')}
            </p>
            <p className="form-cart__item__value">
              {data.package
                ? t(`transactions_page.packages.${data.package.nameInEnglish}`)
                : ''}
            </p>
          </div>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">
              {t('transactions_page.detail.bill.type')}
            </p>
            <p className="form-cart__item__value">Subscription</p>
          </div>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">
              {t('transactions_page.detail.bill.price')}
            </p>
            <p className="form-cart__item__value">
              ฿{parseFloat(data.amount).toLocaleString() || 0}
            </p>
          </div>
          <span className="form-cart__divide --solid" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">
              {t('transactions_page.detail.bill.subtotal')}
            </p>
            <p className="form-cart__item__value">
              ฿{subTotal.toLocaleString()}
            </p>
          </div>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">
              {t('transactions_page.detail.bill.vat', { vat: '7%' })}
            </p>
            <p className="form-cart__item__value">
              {formatCurrency(data.amountVat || 0)}
            </p>
          </div>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <h3 className="form-cart__title">
              {t('transactions_page.detail.bill.total')}
            </h3>
            <h3 className="form-cart__title">
              {formatCurrency(data.amountTotal || 0)}
            </h3>
          </div>
        </div>
        <div className="form-cart">
          <p className="form-cart__item__label">
            {t('transactions_page.detail.purchase_info.title')}
          </p>
          <h3 className="form-cart__title text-capitalize">
            {`${data.customerFirstName} ${data.customerLastName}`}
          </h3>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">{t('common.address')}</p>
            <p className="form-cart__item__label transaction-detail__info">
              {`${address} ${
                data.customerZipcode ? `(${data.customerZipcode})` : ''
              }`}
            </p>
          </div>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">{t('common.tel')}</p>
            <p className="form-cart__item__label transaction-detail__info">
              {data.customerPhone}
            </p>
          </div>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">{t('common.email')}</p>
            <p className="form-cart__item__label transaction-detail__info">
              {data.customerEmail}
            </p>
          </div>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">{t('common.tax_number')}</p>
            <p className="form-cart__item__label transaction-detail__info">
              {data.customerTaxNumber}
            </p>
          </div>
        </div>
      </Col>
      <Col xxl={{ span: 8 }} md={{ span: 10 }} span={24}>
        <div className="form-cart transaction-detail__slip">
          <div>
            <h3 className="form-cart__title">
              {t('transactions_page.detail.payslip.title')}
            </h3>
            {(!data.paymentSlip || resubmit) && (
              <>
                <UploadZone onChangeFile={onChangeFile} />
                <p className="transaction-detail__hint">
                  {t('transactions_page.detail.payslip.upload_file_hint')}
                </p>
              </>
            )}
            {!resubmit && data.paymentSlip && (
              <img
                src={data.paymentSlip}
                alt="slip"
                className="transaction-detail__preview"
                aria-hidden="true"
                onClick={handleOpenImage}
              />
            )}
            <div className="transaction-detail__group-banks">
              <div className="form-payment__bank">
                <span className="form-payment__bank__icon" />
                <div className="form-payment__bank__group">
                  <p className="form-payment__bank__name">Kasikorn Bank</p>
                  <p className="form-payment__bank__phone">
                    153-2-94832-7
                    <br />
                    {t('common.banks.kasikorn.company_name')}
                  </p>
                </div>
              </div>
              {/* <div className='form-payment__bank'>
                <span
                  className='form-payment__bank__icon --kbank'
                />
                <div className='form-payment__bank__group'>
                  <p className='form-payment__bank__name'>Krungthai Bank</p>
                  <p className='form-payment__bank__phone'>1924900334252</p>
                </div>
              </div> */}
            </div>
          </div>
          {(!data.paymentSlip || resubmit) && (
            <ButtonComponent
              type="primary"
              disabled={!slip}
              onClick={handleSubmit}
              loading={loading}
            >
              {t('common.btn_submit')}
            </ButtonComponent>
          )}
          {!resubmit && isRejected && (
            <ButtonComponent type="primary" onClick={() => setResubmit(true)}>
              {t('common.btn_re_submit')}
            </ButtonComponent>
          )}
        </div>
        <div className="form-cart transaction-detail__additional-information">
          <h3 className="form-cart__title text-capitalize">
            {t('transactions_page.detail.additional_information.title')}
          </h3>
          <div className="form-cart__item">
            <p className="form-cart__item__label">
              {t('transactions_page.detail.additional_information.created_by')}
            </p>
            <p className="form-cart__item__label transaction-detail__info">
              {data.user?.email}
            </p>
          </div>
          <span className="form-cart__divide" />
          <div className="form-cart__item">
            <p className="form-cart__item__label">
              {t(
                'transactions_page.detail.additional_information.payment_method',
              )}
            </p>
            <p className="form-cart__item__label transaction-detail__info">
              {data.paymentMethod
                ? t(`common.payment_methods.${data.paymentMethod}`)
                : ''}
            </p>
          </div>
          {data.confirmedAt && (
            <>
              <span className="form-cart__divide" />
              <div className="form-cart__item">
                <p className="form-cart__item__label">
                  {t(
                    'transactions_page.detail.additional_information.approved_date',
                  )}
                </p>
                <p className="form-cart__item__label transaction-detail__info">
                  {data.confirmedAt ? parseDateTime(data.confirmedAt) : ''}
                </p>
              </div>
            </>
          )}
          {data.rejectReason && (
            <>
              <span className="form-cart__divide" />
              <div className="form-cart__item">
                <p className="form-cart__item__label">
                  {t(
                    'transactions_page.detail.additional_information.reject_reason',
                  )}
                </p>
                <p className="form-cart__item__label transaction-detail__info">
                  {data.rejectReason}
                </p>
              </div>
            </>
          )}
          {data.invoiceFile && (
            <ButtonComponent
              className="transaction-detail__download-btn"
              onClick={handleDownload}
              icon={<DownloadIcon />}
            >
              {t('common.btn_download_invoice')}
            </ButtonComponent>
          )}
        </div>
      </Col>
    </Row>
  );
}

TransactionDetailContainer.propTypes = {
  data: PropTypes.object,
};

export default TransactionDetailContainer;
